<template>
  <section ref="pageIntro" v-editable="blok" class="block page-intro">
    <div class="card">
      <div class="container">
        <div class="card__inner">
          <MediaImage
            :is-article-intro="true"
            :media="blok.image"
            class="column sm-5"
            @ready="handleMediaImageReady"
          />
          <div class="card__content column sm-7">
            <div>
              <NavigationBreadcrumbs v-if="breadcrumbs" />
              <h1 v-if="title">
                {{ title }}
              </h1>
              <h2 class="h3 light whitespace-pre-line">
                {{ blok.introduction }}
              </h2>
              <cite v-if="authors?.length" class="author">
                By
                <span v-for="(author, index) in authors" :key="author.uuid">
                  <strong>
                    {{ author.content?.firstname }}
                    {{ author.content?.surname }}
                  </strong>
                  <!-- <span
                    v-if="author.content.organisation"
                    class="text-muted less-muted"
                    >{{ ' (' + author.content.organisation + ')' }}</span
                  > -->
                  <span v-if="index < authors.length - 1"> and </span>
                </span>
              </cite>
              <div v-if="showStickySpacer" class="sticky-spacer" />
              <ArticleStickyNavigation
                ref="stickyNav"
                :content="richTextContent"
                :introduction="introduction"
                :title="title"
              />
            </div>
            <div class="card__meta">
              <span v-if="displayDate" class="date">
                <i class="icon">
                  <Icon name="fa6-regular:clock" />
                </i>
                <strong v-if="showFirstPublished"> Published: </strong>
                <strong v-else> Last updated: </strong>

                {{
                  dateFormat(displayDate, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                }}
              </span>
              <slot name="meta" />
              <LazyVisibleArticleToolButtons
                v-if="metabuttons"
                :comments-button="commentsButton"
                :downloads-button="downloadsButton"
                :introduction="blok.introduction"
                :title="title"
                hydrate-on-visible
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <slot />
  </section>
</template>

<script setup lang="ts">
import type {
  PageIntroStoryblok,
  ContentTypeAuthorStoryblok,
} from '~/types/storyblok'
//import ScrollListener from '~/mixins/scroll-listener'
import { dateFormat } from '#imports'

const router = useRouter()
const route = useRoute()

const pageIntro = useTemplateRef('pageIntro')
const stickyNav = useTemplateRef('stickyNav')

const props = withDefaults(
  defineProps<{
    blok: PageIntroStoryblok
    metabuttons?: boolean
    breadcrumbs?: boolean
    downloadsButton?: boolean
    commentsButton?: boolean
    authors?: ContentTypeAuthorStoryblok[] | null
  }>(),
  {
    metabuttons: false,
    breadcrumbs: true,
    downloadsButton: false,
    commentsButton: false,
    authors: null,
  },
)

const { story } = storeToRefs(useStoryProviderStore())
const { title, introduction } = useStoryContent(toRefs(props))
const { displayDate, showFirstPublished } = usePage()
const richTextContent = computed(() => story.value?.content?.content)

const {
  showStickySpacer,
  setupStickyNav,
  cleanupStickyNav,
  updateInitialPositions,
} = useStickyNav({
  stickyNav,
})

// need calc after media image is ready, otherwise the sticky nav calc may be wrong
function handleMediaImageReady() {
  nextTick(() => {
    updateInitialPositions()
  })
}

// Watch for content changes that might affect layout
watch(
  () => richTextContent.value,
  () => {
    nextTick(() => {
      updateInitialPositions()
    })
  },
  { deep: true },
)

onMounted(() => {
  setupStickyNav()
  nextTick(() => {
    updateInitialPositions()
  })
})

onUnmounted(() => {
  cleanupStickyNav()
})

// when at the top of the page (when the page intro is mostly visible), remove the hash from the url
const pageIntroIsVisible = useElementVisibility(pageIntro, {
  threshold: 0.9,
})
watch(pageIntroIsVisible, onPageIntroVisible)
function onPageIntroVisible(isVisible: boolean) {
  // console.log('onPageIntroVisible', isVisible)
  if (isVisible) {
    router.push({
      hash: '',
      query: route.query,
    })
  }
}
</script>

<style lang="less" scoped>
.page-intro {
  .relative();

  .card {
    background: @color-fill-normal;

    .container {
      padding: 0;
    }

    .card__inner {
      .flex-col();

      background: transparent;

      @media @tablet {
        .flex-row();
      }

      .card__content {
        height: auto;
        max-width: @720px;
        padding: @gutter-lg @gutter @gutter;

        @media @tablet {
          order: 1;
          padding: @gutter-xl @gutter @gutter-xl;
        }

        div,
        .card__meta {
          @media @tablet {
            padding-left: @gutter-lg;
          }

          @media @desktop {
            padding-left: @gutter-xl;
          }
        }

        .card__meta {
          .text-body();
          .justify-between();
          .align-center();

          i.icon {
            color: inherit;
          }

          > *:not(.article-tool-buttons) {
            margin-top: @gutter-sm;
            margin-bottom: @gutter-sm;
          }

          > .article-tool-buttons {
            margin: 0;
            padding: 0;
            border: none;
          }
        }

        h1 {
          margin-top: unit(24/42, em); // 24px
        }
      }
    }
  }

  .sticky-spacer {
    height: @sticky-nav-height;
    width: 100%;
    display: block;
    visibility: hidden;
  }
}

@media print {
  .page-intro {
    margin-top: @gutter-xs;
    padding: @gutter-lg @gutter-sm;
    border: solid @2px @color-border;

    .card {
      .card__inner {
        .flex-row();

        padding: 0 @gutter;

        .media {
          flex-basis: 30%;
        }
        .card__content {
          padding: 0 @gutter-xl 0 0;
          flex-basis: 70%;

          .card__meta {
            display: none;
          }

          h1 {
            margin-top: 0;
          }
        }
      }
    }
    .event-date-overlay {
      display: none;
    }
  }
}
</style>
